import {FIREBASE_VAPID_KEY, getMessagingIfSupported} from "@/lib/firebase/config";
import {safeLocalStorage} from "@/lib/utils/storage";

/**
 * Leaf module for local-only FCM token teardown — AC-649. It imports NO RTK /
 * api code (only firebase config), so it is safe to import from the baseQuery
 * layer (lib/api/utils.ts) without a circular dependency. `firebase/messaging`
 * itself is `import()`-ed inside the function below rather than statically —
 * this module is reachable from every RTK Query endpoint via that baseQuery
 * layer, so a static SDK import here would ship it in the shell for every
 * visitor regardless of whether they ever log out (AC-803).
 */

export const FCM_TOKEN_STORAGE_KEY = "fcm_web_token";

/**
 * Dedicated scope of the FCM service worker. Lives here (not in webPush.ts,
 * which imports RTK) so the teardown path can resolve the registration without
 * a circular dependency. The /firebase-messaging-sw.js response carries
 * `Service-Worker-Allowed: /firebase-push/`, deliberately confining the FCM
 * worker to this scope — the root scope belongs to the PWA's own worker.
 */
export const FCM_SW_SCOPE = "/firebase-push/";

/**
 * Deletes the FCM token locally so the browser stops receiving pushes, and
 * clears local storage. Returns the token value so a caller with a still-valid
 * session can additionally DEACTIVATE it on the backend (see LogOutForm).
 * Best-effort and guarded: no-op on the server or when FCM is
 * unconfigured/unsupported; never throws.
 *
 * On forced logout (refresh failure / ban) the backend DELETE is skipped — the
 * Bearer is already dead — but the local teardown invalidates the token at FCM,
 * so the next backend send gets UNREGISTERED and auto-deactivates the DB row.
 *
 * The messaging instance must be bound to OUR /firebase-push/ registration
 * before deleteToken: a bare deleteToken() falls back to registering the
 * default root-scope worker, which Service-Worker-Allowed intentionally blocks
 * (messaging/failed-service-worker-registration on every PWA logout, and the
 * token survived). getToken with the registration only binds/returns the
 * existing token — guarded by permission === "granted" so it never prompts.
 */
export async function unregisterWebPushTokenLocally(): Promise<string | null> {
    if (typeof window === "undefined") return null;
    const token = safeLocalStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    safeLocalStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    try {
        const swReg = await navigator.serviceWorker?.getRegistration(FCM_SW_SCOPE);
        if (!swReg) return token; // FCM worker was never registered here — nothing to tear down

        const messaging = await getMessagingIfSupported();
        if (messaging && Notification.permission === "granted") {
            const {deleteToken, getToken} = await import("firebase/messaging");
            await getToken(messaging, {
                vapidKey: FIREBASE_VAPID_KEY,
                serviceWorkerRegistration: swReg,
            });
            await deleteToken(messaging);
        } else {
            // Permission revoked or messaging unsupported: kill the raw push
            // subscription instead — FCM invalidates the token upstream and the
            // next backend send auto-deactivates it via UNREGISTERED.
            const sub = await swReg.pushManager.getSubscription();
            await sub?.unsubscribe();
        }
    } catch (err) {
        console.error("[FCM] local token delete failed", err);
    }
    return token;
}
